import React, { useState } from 'react';
import { Users } from 'lucide-react';

interface SFTConfigProps {
  config: {
    nombreEnfants: number;
  };
  onChange: (config: { nombreEnfants?: number }) => void;
  currentIndiceMajore?: number | null;
}

export default function SFTConfig({ config, onChange, currentIndiceMajore }: SFTConfigProps) {
  const [showMoreThan5, setShowMoreThan5] = useState(config.nombreEnfants > 5);

  const handleEnfantsChange = (nombreEnfants: number) => {
    onChange({ nombreEnfants });
    if (nombreEnfants <= 5) {
      setShowMoreThan5(false);
    }
  };

  const handleMoreThan5Toggle = () => {
    setShowMoreThan5(!showMoreThan5);
    if (!showMoreThan5 && config.nombreEnfants <= 5) {
      onChange({ nombreEnfants: 6 });
    }
  };

  const calculateSFT = () => {
    if (!currentIndiceMajore || config.nombreEnfants === 0) return { partFixe: 0, partProportionnelle: 0, total: 0 };

    // Parts fixes
    let partFixe = 0;
    if (config.nombreEnfants === 1) {
      partFixe = 2.29;
    } else if (config.nombreEnfants === 2) {
      partFixe = 10.67;
    } else if (config.nombreEnfants === 3) {
      partFixe = 15.24;
    } else if (config.nombreEnfants > 3) {
      partFixe = 15.24 + ((config.nombreEnfants - 3) * 4.57);
    }

    // Part proportionnelle (à partir de 2 enfants)
    let partProportionnelle = 0;
    if (config.nombreEnfants >= 2) {
      const indicePlafonne = Math.min(722, Math.max(454, currentIndiceMajore));
      const traitementMensuel = (indicePlafonne * 5907.34) / 1200;
      
      let tauxProportionnel = 0;
      if (config.nombreEnfants === 2) {
        tauxProportionnel = 0.03;
      } else if (config.nombreEnfants === 3) {
        tauxProportionnel = 0.08;
      } else if (config.nombreEnfants > 3) {
        tauxProportionnel = 0.08 + ((config.nombreEnfants - 3) * 0.06);
      }
      
      partProportionnelle = traitementMensuel * tauxProportionnel;
    }

    return {
      partFixe,
      partProportionnelle,
      total: partFixe + partProportionnelle
    };
  };

  const sftCalculation = calculateSFT();

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="w-4 h-4 inline mr-1" />
          Nombre d'enfants à charge (moins de 20 ans)
        </label>
        
        {!showMoreThan5 ? (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              {[0, 1, 2, 3, 4, 5].map((nb) => (
                <button
                  key={nb}
                  onClick={() => handleEnfantsChange(nb)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    config.nombreEnfants === nb
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {nb}
                </button>
              ))}
            </div>
            <button
              onClick={handleMoreThan5Toggle}
              className="text-sm text-blue-600 hover:text-blue-700 underline"
            >
              Plus de 5 enfants ?
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={config.nombreEnfants}
                onChange={(e) => handleEnfantsChange(Math.max(0, Math.min(20, Number(e.target.value))))}
                min="0"
                max="20"
                step="1"
                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-600">enfants</span>
            </div>
            <button
              onClick={handleMoreThan5Toggle}
              className="text-sm text-blue-600 hover:text-blue-700 underline"
            >
              Retour à la sélection rapide
            </button>
          </div>
        )}
      </div>
    </div>
  );
}